import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import Auth from '../../../Helpers/auth';
import HelmetHelper from '../../../Components/PageHelmet/HelmetHelper.component.jsx';
import {
  AuthPanel,
  AuthHeader,
  AuthTitle,
  AuthDescription,
  ModeToggle,
  ModeButton,
  FormFields,
  // NameRow,
  FieldGroup,
  FieldLabel,
  FieldInput,
  ErrorMessage,
  CheckoutAction,
  PageWrapper,
  BackButton,
} from './CheckoutAuth.styles';
import {
  httpLoginUser,
  httpSignUpUser,
  httpAddToCart,
} from '../../../Hooks/user/user.post.hooks';
import {
  getGuestCart,
  clearGuestCart,
} from '../../../Routes/MainComponents/navigation/GuestCart';
import PrimaryButtonComponent from '../../../Components/Buttons/PrimaryButton/PrimaryButton.component';

const CheckoutAuth = () => {
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    verifyPassword: '',
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showVerify, setShowVerify] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    if (error) setError('');
  };

  const mergeGuestCart = async () => {
    try {
      const userId = Auth.getProfile()?.data?.id;
      const guestCart = getGuestCart() || [];
      const hadItems = Array.isArray(guestCart) && guestCart.length > 0;
      await Promise.all(
        guestCart.map((item) =>
          httpAddToCart({
            courseId: item.courseId || item._id || item.id,
            userId,
            itemType: 'course',
            quantity: item.quantity || 1,
          })
        )
      );
      clearGuestCart();
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) && query.queryKey[0] === 'cart',
      });
      try {
        window.dispatchEvent(new Event('guest-cart-updated'));
      } catch (err) {
        console.log('Error dispatching guest-cart-updated event:', err);
      }
      if (hadItems) {
        try {
          sessionStorage.setItem('guest_cart_merged', '1');
        } catch (e) {
          /* ignore sessionStorage failures */
        }
      }
      return hadItems;
    } catch (err) {
      console.error('Error merging guest cart:', err);
      return false;
    }
  };

  const handleLogin = async () => {
    setIsSubmitting(true);
    try {
      const resp = await httpLoginUser({
        email: form.email.trim(),
        password: form.password,
      });
      const data = await resp.json();
      if (!resp.ok) {
        setError(data.msg || data.error || 'Login failed');
        setIsSubmitting(false);
        return;
      }
      Auth.login(data.token);
      try {
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) && query.queryKey[0] === 'cart',
        });
      } catch (err) {
        console.log('Error invalidating cart query:', err);
      }
      await mergeGuestCart();
      setIsSubmitting(false);
      navigate('/cart');
      //   navigate('/checkout');
    } catch (err) {
      setError(err.message || 'Login error');
      setIsSubmitting(false);
    }
  };

  const handleSignup = async () => {
    setIsSubmitting(true);
    if (form.password !== form.verifyPassword) {
      setError('Passwords do not match');
      setIsSubmitting(false);
      return;
    }
    try {
      const payload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        password: form.password,
      };
      const resp = await httpSignUpUser(payload);
      const data = await resp.json();
      if (!resp.ok) {
        setError(data.msg || data.error || 'Signup failed');
        setIsSubmitting(false);
        return;
      }
      Auth.login(data.token);
      try {
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) && query.queryKey[0] === 'cart',
        });
      } catch (err) {
        console.log('Error invalidating cart query:', err);
      }
      await mergeGuestCart();
      setIsSubmitting(false);
      navigate('/cart');
      //   navigate('/checkout');
    } catch (err) {
      setError(err.message || 'Signup error');
      setIsSubmitting(false);
    }
  };

  return (
    <PageWrapper>
      <HelmetHelper
        title="Checkout Authentication"
        description="Log in or create an account to continue with your course purchase."
        noIndex
      />
      <div style={{ width: '100%', maxWidth: '28rem' }}>
        <BackButton onClick={() => navigate('/cart')}>
          &larr; Back to cart
        </BackButton>
        <AuthPanel>
          <AuthHeader>
            <AuthTitle>
              {mode === 'login' ? 'Log in to continue' : 'Create your account'}
            </AuthTitle>

            <AuthDescription>
              {mode === 'login'
                ? 'In order to purchase courses you must login or create an account to be able to access your courses.'
                : 'Create an account so you can access your purchase after checkout.'}
            </AuthDescription>
          </AuthHeader>

          <ModeToggle role="tablist" aria-label="Account options">
            <ModeButton
              type="button"
              $active={mode === 'login'}
              onClick={() => setMode('login')}
              role="tab"
              aria-selected={mode === 'login'}
            >
              Log in
            </ModeButton>

            <ModeButton
              type="button"
              $active={mode === 'signup'}
              onClick={() => setMode('signup')}
              role="tab"
              aria-selected={mode === 'signup'}
            >
              Create account
            </ModeButton>
          </ModeToggle>

          <FormFields>
            {mode === 'signup' && (
              <>
                <FieldGroup>
                  <FieldLabel htmlFor="firstName">First name</FieldLabel>
                  <FieldInput
                    id="firstName"
                    name="firstName"
                    placeholder="First name"
                    value={form.firstName}
                    onChange={handleChange}
                    autoComplete="given-name"
                  />
                </FieldGroup>

                <FieldGroup>
                  <FieldLabel htmlFor="lastName">Last name</FieldLabel>
                  <FieldInput
                    id="lastName"
                    name="lastName"
                    placeholder="Last name"
                    value={form.lastName}
                    onChange={handleChange}
                    autoComplete="family-name"
                  />
                </FieldGroup>
              </>
            )}

            <FieldGroup>
              <FieldLabel htmlFor="email">Email</FieldLabel>
              <FieldInput
                id="email"
                name="email"
                type="email"
                placeholder="you@example.com"
                value={form.email}
                onChange={handleChange}
                autoComplete="email"
              />
            </FieldGroup>

            <FieldGroup>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <FieldLabel htmlFor="password">Password</FieldLabel>
                <button
                  type="button"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  onClick={() => setShowPassword((s) => !s)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--grey5)',
                    fontWeight: 700,
                  }}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              <FieldInput
                id="password"
                name="password"
                placeholder="Enter your password"
                value={form.password}
                onChange={handleChange}
                type={showPassword ? 'text' : 'password'}
                autoComplete={
                  mode === 'login' ? 'current-password' : 'new-password'
                }
              />
            </FieldGroup>

            {mode === 'signup' && (
              <FieldGroup>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <FieldLabel htmlFor="verifyPassword">
                    Confirm password
                  </FieldLabel>
                  <button
                    type="button"
                    aria-label={
                      showVerify
                        ? 'Hide confirm password'
                        : 'Show confirm password'
                    }
                    onClick={() => setShowVerify((s) => !s)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'var(--grey5)',
                      fontWeight: 700,
                    }}
                  >
                    {showVerify ? 'Hide' : 'Show'}
                  </button>
                </div>
                <FieldInput
                  id="verifyPassword"
                  name="verifyPassword"
                  placeholder="Re-enter your password"
                  value={form.verifyPassword}
                  onChange={handleChange}
                  type={showVerify ? 'text' : 'password'}
                  autoComplete="new-password"
                />
              </FieldGroup>
            )}
          </FormFields>

          {error && <ErrorMessage role="alert">{error}</ErrorMessage>}

          <CheckoutAction>
            <PrimaryButtonComponent
              span={
                mode === 'login'
                  ? isSubmitting
                    ? 'Signing in...'
                    : 'Sign In'
                  : isSubmitting
                    ? 'Creating account...'
                    : 'Create Account'
              }
              functionName={mode === 'login' ? handleLogin : handleSignup}
              disabled={isSubmitting}
            />
          </CheckoutAction>
        </AuthPanel>
      </div>
    </PageWrapper>
  );
};

export default CheckoutAuth;
